import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { ConversionQuestionOptions, ProblemData } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { conversionTable as esTable } from "../Files/Prompts/es/conversion.ts";
import { conversionTable as frTable } from "../Files/Prompts/fr/conversion.ts";

/**
 * Unit conversion. See ADR 0032 §4.
 *
 * Every conversion is exact and every answer is a whole number, which is the
 * decision that shapes the file: `250 cm = 2.5 m` is a decimals question
 * wearing a conversion's clothes, and Decimals is already a format with its
 * own place-count setting. So the problem is built answer-first and multiplied
 * up, and there is nothing to reject.
 *
 * The consequence worth knowing is that converting *down* the scale
 * (km to m) is always easy and converting *up* is where students go wrong, so
 * both directions are drawn. `ConversionGenerator.test.ts` asserts both appear:
 * a generator that quietly only went downward would pass every correctness
 * check and teach half the skill.
 *
 * Units are written plural throughout because the multiplier is never 1 - see
 * `SMALLEST_MULTIPLIER`. That is what lets this file avoid `Grammar.ts`
 * entirely.
 */

/** How many of `small` make one `large`. Every factor is a whole number. */
export interface ConversionPair {
    small: string;
    large: string;
    factor: number;
}

/**
 * The site's first unit table. Data, not logic.
 *
 * ADR 0032 admits exactly these three systems, and says so because the
 * temptation later is to grow this toward every unit in existence.
 */
export const CONVERSION_TABLE: Record<string, ConversionPair[]> = {
    [ConversionQuestionOptions.Metric]: [
        { small: "mm", large: "cm", factor: 10 },
        { small: "cm", large: "m", factor: 100 },
        { small: "m", large: "km", factor: 1000 },
        { small: "mm", large: "m", factor: 1000 },
        { small: "mL", large: "L", factor: 1000 },
        { small: "g", large: "kg", factor: 1000 },
    ],
    [ConversionQuestionOptions.Customary]: [
        { small: "inches", large: "feet", factor: 12 },
        { small: "feet", large: "yards", factor: 3 },
        { small: "inches", large: "yards", factor: 36 },
        { small: "ounces", large: "pounds", factor: 16 },
        { small: "cups", large: "pints", factor: 2 },
        { small: "pints", large: "quarts", factor: 2 },
        { small: "quarts", large: "gallons", factor: 4 },
        { small: "cups", large: "quarts", factor: 4 },
        { small: "pints", large: "gallons", factor: 8 },
    ],
    [ConversionQuestionOptions.Time]: [
        { small: "seconds", large: "minutes", factor: 60 },
        { small: "minutes", large: "hours", factor: 60 },
        { small: "hours", large: "days", factor: 24 },
        { small: "days", large: "weeks", factor: 7 },
    ],
};

/**
 * The unit table by printed language (ADR 0051 §7). The metric abbreviations
 * are the same table in every language - they are international, not English
 * words - so only Customary and Time need translated tables (`es`/`fr.ts`).
 */
const CONVERSION_TABLE_BY_LANGUAGE: Record<PrintedLanguage, Record<string, ConversionPair[]>> = {
    en: CONVERSION_TABLE,
    es: esTable,
    fr: frTable,
};

/**
 * Never one. `1 km = 1000 m` is a fact to recall rather than a conversion to
 * do, and it would also make every unit singular - see the note at the top.
 */
const SMALLEST_MULTIPLIER = 2;
const LARGEST_MULTIPLIER = 12;

/** Which systems a setting draws from. Mixed means all three. */
function systemsFor(question: ConversionQuestionOptions): ConversionQuestionOptions[] {
    if (question === ConversionQuestionOptions.Mixed) {
        return [
            ConversionQuestionOptions.Metric,
            ConversionQuestionOptions.Customary,
            ConversionQuestionOptions.Time,
        ];
    }
    return [question];
}

/**
 * One conversion, in one direction.
 *
 * Down the scale: the drawn count is in large units and the answer is that
 * times the factor. Up the scale: the drawn count *is* the answer, and the
 * prompt is that times the factor - which is what makes the division exact
 * without having to check it.
 */
export function buildConversionProblem(
    system: ConversionQuestionOptions,
    key: number,
    language: PrintedLanguage = 'en'
): ProblemData {
    const table = CONVERSION_TABLE_BY_LANGUAGE[language];
    const pairs = table[system] ?? table[ConversionQuestionOptions.Metric];
    const pair = pairs[getRandomInt(0, pairs.length - 1)];
    const count = getRandomInt(SMALLEST_MULTIPLIER, LARGEST_MULTIPLIER);
    const goingDown = getRandomInt(0, 1) === 0;

    const prompt = goingDown
        ? `${count} ${pair.large} =`
        : `${count * pair.factor} ${pair.small} =`;
    const answer = goingDown ? String(count * pair.factor) : String(count);

    return {
        key,
        numbers: [],
        operator: "",
        display: [prompt, answer],
        // The unit the blank is followed by. Its own field, never numbers[3].
        conversionUnit: goingDown ? pair.small : pair.large,
        conversionQuestion: system,
    };
}

/** A page-shaped set of unit conversion problems. */
export function generateConversionPages(settings: ISettingsProps): ProblemData[][] | null {
    const systems = systemsFor(settings.ConversionQuestion ?? ConversionQuestionOptions.Mixed);
    const language = printedLanguageOf(settings);
    return generateProblemPages({
        settings,
        choices: systems,
        build: (system, key) => buildConversionProblem(system, key, language),
        fingerprint: (problem) => `${problem.display![0]} ${problem.conversionUnit}`,
    });
}
